import { Alert, Button, Theme, Typography } from "@outschool/backpack";
import { perStudentPriceInCents } from "@outschool/business-rules";
import { EnrollModalType } from "@outschool/data-schemas";
import {
  TrackingEvent,
  makeBannerTrackingName,
} from "@outschool/funding-programs-shared";
import {
  EnrollmentInput,
  PricingOfferState,
} from "@outschool/gql-backend-generated";
import {
  EnrollmentMeetingPurchaseQueryQuery,
  GetLearnerItems_EnrollmentFragment,
  ParentChildrenQuery,
} from "@outschool/gql-frontend-generated";
import { Trans, useTranslation } from "@outschool/localization";
import { useProductAdded, useTrackEvent } from "@outschool/ui-analytics";
import { useFundingProgramAbbr, useSession } from "@outschool/ui-auth";
import {
  LoadingModal,
  ModalActionButtons,
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";

import * as Env from "../../../shared/Env";
import { useCharterParentChild } from "../../hooks/lib/useCharterParentChild";
import { PrepayType } from "../../routes/EnrollmentMeetingPurchasePage";
import ErrorMessage from "../ErrorMessage";
import { LEARNER_NAME_MAX_LENGTH } from "../learner/LearnerNameWarning";
import ApplyPromoCode from "./enrollmentForm/ApplyPromoCode";
import CharterSchoolEnrollForm from "./enrollmentForm/CharterSchoolEnrollForm";
import OrderInputs from "./enrollmentForm/OrderInputs";
import PriceBreakdown from "./enrollmentForm/PriceBreakdown";
import PriceCreditsBreakdown from "./enrollmentForm/PriceCreditsBreakdown";
import ScheduledSessionsDropdown from "./enrollmentForm/ScheduledSessionsDropdown";
import { WarningMessages } from "./enrollmentForm/WarningMessages";
import {
  EnrollmentMeetingPurchaseCharterInput,
  EnrollmentMeetingPurchaseQueryType,
} from "./EnrollmentMeetingPurchaseWithStripe";
import { EsaEnrollmentIneligibleAlert } from "./EsaEnrollmentIneligibleAlert";
import { useOrderContext } from "./OrderContext";
import { EnrollmentOrgTeacher } from "./types";

export type EnrollmentMeetingPurchaseFormProps = {
  canPayLater: EnrollmentMeetingPurchaseQueryType["canPayLater"];
  // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  activity: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"];
  lastSubmittedEnrollments: GetLearnerItems_EnrollmentFragment[];
  enrollingParent?: EnrollmentMeetingPurchaseQueryQuery["currentUser"];
  onClose(e: React.SyntheticEvent): void;
  error: string;
  disabled: boolean;
  disableLearnerSelect?: boolean;
  enrollmentInProgress: boolean;
  requestAgeRangeException: (LearnerItemType: $TSFixMe) => void;
  onEnrollClick?(
    enrollmentInputs: EnrollmentInput[],
    charterParentInput?: EnrollmentMeetingPurchaseCharterInput | null,
  ): void;
  disableOnSubmit?: boolean;
  nextBillingDate?: Date;
  disableGoBack?: Date;
  ignoreStrictAgeRange: boolean;
  orgTeacher?: EnrollmentOrgTeacher;
  prepayType?: PrepayType | null;
  setParentByEmail: React.Dispatch<
    React.SetStateAction<ParentChildrenQuery["parentByEmail"] | null>
  >;
  nextButtonText: string;
  enrollmentDuration?: number | null;
  isThreeStepBooking?: boolean;
  startTime?: Date;
  endTime?: Date;
  useStickyFooter?: boolean;
};

export default function EnrollmentMeetingPurchaseForm({
  activity,
  enrollingParent,
  onClose,
  error,
  disabled,
  disableLearnerSelect,
  enrollmentInProgress,
  onEnrollClick = () => {},
  requestAgeRangeException,
  canPayLater = false,
  disableOnSubmit = true,
  nextBillingDate,
  ignoreStrictAgeRange = false,
  orgTeacher,
  prepayType,
  setParentByEmail,
  nextButtonText,
  enrollmentDuration,
  isThreeStepBooking = false,
  startTime,
  endTime,
}: EnrollmentMeetingPurchaseFormProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\EnrollmentMeetingPurchaseForm",
  );
  const trackProductAdded = useProductAdded();
  const [highlightErrors, setHighlightErrors] = React.useState<boolean>(false);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const esaState = useFundingProgramAbbr();
  const trackEvent = useTrackEvent();
  const {
    enrollmentInputs,
    buyingLearners,
    setLearnerItems,
    pricingBreakdown,
    learnerItems,
    hasEsaMembership,
  } = useOrderContext();
  const { currentUser, destroyEsaSession, currentUserRefetch } = useSession();
  const [localError, setLocalError] = React.useState<string>();
  const [charterEnrollmentInfo, setCharterEnrollmentInfo] =
    React.useState<EnrollmentMeetingPurchaseCharterInput | null>(null);
  const { pricingOffer, pricingOfferState } = pricingBreakdown ?? {};
  const isPricingOfferValid =
    pricingOfferState === PricingOfferState.ValidFinancialAid;
  const hasBackgroundCheckExpired = orgTeacher
    ? !!orgTeacher.hasBackgroundCheckExpired
    : !!activity.leader?.hasBackgroundCheckExpired;
  const isPrepayEnrollment = !!prepayType;

  const parentByEmail = useCharterParentChild(
    charterEnrollmentInfo?.email,
    null,
  );

  React.useEffect(() => {
    setParentByEmail(parentByEmail || null);
  }, [parentByEmail, setParentByEmail]);

  // check if the learner is already enrolled in the activity with a different (org) teacher than
  // the one they are trying to enroll with and, if true, disable the learner item to prevent enrolling
  const updatedLearnerItems = React.useMemo(() => {
    if (!!orgTeacher) {
      const updatedLearnerItems = learnerItems.map(learnerItem => {
        const learnerItemOtherSectionEnrollmentTeacherUid =
          learnerItem.otherSectionEnrollment?.section?.leader?.uid;

        return {
          ...learnerItem,
          disabled:
            (!!learnerItemOtherSectionEnrollmentTeacherUid &&
              learnerItemOtherSectionEnrollmentTeacherUid !== orgTeacher.uid) ||
            learnerItem.disabled,
        };
      });

      if (!_.isEqual(updatedLearnerItems, learnerItems)) {
        return updatedLearnerItems;
      }
    }

    return null;
  }, [learnerItems, orgTeacher]);

  React.useEffect(() => {
    if (!!orgTeacher && updatedLearnerItems) {
      setLearnerItems(updatedLearnerItems);
    }
  }, [orgTeacher, updatedLearnerItems, setLearnerItems]);

  const hasCharterParentEmailAndName =
    !isPrepayEnrollment ||
    prepayType === PrepayType.ClassWallet ||
    charterEnrollmentInfo?.isValid;

  const disableForm =
    disabled ||
    enrollmentInProgress ||
    submitted ||
    hasBackgroundCheckExpired ||
    !hasCharterParentEmailAndName;

  const validBuyingLearnerNames = buyingLearners.every(
    learner =>
      learner.name &&
      (learner.uid || learner.name.length <= LEARNER_NAME_MAX_LENGTH),
  );

  const submit = (e: MouseEvent) => {
    e.preventDefault();

    if (disableForm || !validBuyingLearnerNames) {
      return;
    }

    if (!buyingLearners.length) {
      setLocalError(t`Please select the learner you’d like to enroll`);
      return;
    }

    const validBuyingLearnersCount = buyingLearners.filter(
      learner => learner.name && learner.name.trim() !== "" && !!learner.age,
    ).length;
    const hasValidBuyingLearners =
      validBuyingLearnersCount > 0 &&
      validBuyingLearnersCount === buyingLearners.length;

    if (!hasValidBuyingLearners) {
      setHighlightErrors(true);
      return;
    }

    if (disableOnSubmit) {
      setSubmitted(true);
    }
    const enrollmentInputsAdjusted: EnrollmentInput[] = enrollmentInputs.map(
      enrollmentInput => {
        const price_cents = enrollmentInput.price_cents;
        return {
          ...enrollmentInput,
          // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
          pricing_offer: isPricingOfferValid ? pricingOffer.name : null,
          price_cents,
        };
      },
    );

    onEnrollClick(enrollmentInputsAdjusted, charterEnrollmentInfo);
  };

  React.useEffect(() => {
    if (!enrollingParent) {
      return;
    }
    trackProductAdded({
      activityListing: { activity, leader: activity.leader },
      user: enrollingParent,
      pricingOffer: isPricingOfferValid ? pricingOffer?.name : undefined,
      enrollModalType: EnrollModalType.one_on_one,
      enrollmentDuration:
        enrollmentDuration === null
          ? "UNTIL_I_CANCEL"
          : enrollmentDuration?.toString(),
    });
  }, [
    activity,
    isPricingOfferValid,
    enrollingParent,
    pricingOffer?.name,
    trackProductAdded,
    enrollmentDuration,
  ]);

  if (!activity || !activity.details || !currentUser) {
    return <LoadingModal />;
  }

  const priceCents = perStudentPriceInCents(activity);

  const isCharterEnrollment =
    isPrepayEnrollment &&
    !!setCharterEnrollmentInfo &&
    (prepayType === PrepayType.CharterAdmin ||
      prepayType === PrepayType.CharterPO);

  const ScheduledSessions =
    startTime !== undefined &&
    endTime !== undefined &&
    enrollmentDuration !== undefined &&
    activity?.uid !== undefined;
  return (
    <form method="POST" onSubmit={e => e.preventDefault()}>
      {currentUser.hasSubscription &&
        !currentUser.canPayWithSubscriptionCredits && (
          <Alert severity={"info"} sx={{ marginBottom: "1rem" }}>
            <Typography variant="body2" color="inherit">
              <Trans t={t}>
                Membership credits aren’t available in a ClassWallet session. To
                use your credits,{" "}
                <Button
                  variant="link"
                  sx={(theme: Theme) => ({
                    fontSize: theme.typography.button,
                    fontWeight: "400",
                    verticalAlign: "unset",
                  })}
                  onClick={() => {
                    trackEvent(
                      makeBannerTrackingName(TrackingEvent.OptOutTouch),
                      {
                        fundingProgram: esaState,
                        component: "SubscriptionCreditNavItem",
                      },
                    );
                    destroyEsaSession();
                    currentUserRefetch();
                  }}
                >
                  {t("exit the ClassWallet session")}.
                </Button>
              </Trans>
            </Typography>
          </Alert>
        )}

      {!enrollingParent?.canPayWithSubscriptionCredits && (
        <WarningMessages
          enrollingParent={enrollingParent}
          pricingOfferState={pricingOfferState}
          pricingOffer={pricingOffer}
        />
      )}
      {(error || localError) && (
        <ErrorMessage
          value={error || localError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      )}
      {hasBackgroundCheckExpired && (
        <ErrorMessage
          value={t`This educator is not accepting new enrollments at this time.  Please message the educator with your request or check back soon. Thank you for your continued interest in our programs.`}
        />
      )}

      {isCharterEnrollment && (
        <CharterSchoolEnrollForm
          setCharterEnrollmentInfo={setCharterEnrollmentInfo}
          prepayType={prepayType}
        />
      )}

      <OrderInputs
        disabled={disableForm || !!disableLearnerSelect}
        activity={activity}
        ignoreStrictAgeRange={ignoreStrictAgeRange}
        learnerItems={learnerItems}
        buyingLearners={buyingLearners}
        onLearnerItemsUpdate={setLearnerItems}
        highlightErrors={highlightErrors}
        requestAgeRangeException={requestAgeRangeException}
        onlyAllowSingleLearner={true}
        isThreeStepBooking={isThreeStepBooking}
        usingEsaFunds={hasEsaMembership}
      />

      {!!pricingBreakdown && (
        <EsaEnrollmentIneligibleAlert pricingBreakdown={pricingBreakdown} />
      )}

      {!canPayLater && <ApplyPromoCode isOneOnOne={true} />}

      {ScheduledSessions && isThreeStepBooking && (
        <ScheduledSessionsDropdown
          activity={activity}
          startTime={startTime}
          endTime={endTime}
          enrollmentDuration={enrollmentDuration}
          orgTeacher={orgTeacher}
        />
      )}
      {!currentUser.canPayWithSubscriptionCredits && (
        <PriceBreakdown
          canPayLater={canPayLater}
          activity={activity}
          enrollmentInputs={enrollmentInputs}
          priceCents={priceCents}
          isBuyoutSection={false}
          isMeetingPrepay={isPrepayEnrollment}
          oneOnOneNextBillingDate={nextBillingDate}
        />
      )}

      {currentUser.canPayWithSubscriptionCredits && (
        <PriceCreditsBreakdown
          activity={activity}
          canPayLater={canPayLater}
          oneOnOneNextBillingDate={nextBillingDate}
        />
      )}

      <ModalActionButtons
        cancelLabel={t("Go Back")}
        onCancel={onClose}
        disabled={disableForm || !validBuyingLearnerNames}
        primaryLabel={nextButtonText}
        immediatePrimaryAction={submit}
        trackingName="continue_to_payment"
        trackingSharedProperties={{
          activityUid: activity.uid,
        }}
        sx={{
          marginTop: "1em",
          paddingTop: "1em",
          borderTop: "1px solid #CCCCCC",
        }}
      />
    </form>
  );
}
